import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { useLocation } from 'react-router-dom'
import { spring } from '../lib/springs'
import { useSurface, SurfaceProvider } from '../lib/surface-context'
import { surfaceClasses } from '../lib/surface-classes'

/* Shared motion vocabulary for the sidebar, now aliases of the motion tiers
   in lib/springs.js: anything that changes size rides `moderate`, the hover
   pill `fast`. New code imports `spring` from lib/springs directly; these
   names stay because the shell still imports them. EASE_OUT_QUINT is kept
   for the same reason. */
export const SPRING = spring.moderate
export const PILL_SPRING = spring.fast
export const EASE_OUT_QUINT = [0.23, 1, 0.32, 1]

/* ============================================================================
   Footer menu that MORPHS out of its own trigger row instead of dropping a
   separate panel.

     <div .sb-menu>                  holds the row's place in the footer
       <button .{triggerClassName}>  the row itself, always on top
       <motion.div .sb-menu-card>    absolute, bottom-left anchored
         body                        height 0 -> auto, grows upward
         tail                        the row's footprint (+ railLabel on the rail)

   Closed, the card is invisible and exactly the row's size. Opening fades its
   chrome in, springs its height up from the row and — on the 60px rail —
   springs its width out to `railWidth`, so the row looks like it unfolds into
   the menu. The trigger comes first in the DOM so Tab moves from it into the
   menu even though the menu is painted above it.

   Closes on outside mousedown, Escape (focus returns to the trigger), a route
   change, and a collapse toggle (the card's widths depend on the mode).

   Uncontrolled by default; pass `open` + `onOpenChange` to open it from
   elsewhere (the notification stack's "View all" opens the bell's menu).
   ========================================================================== */
const SidebarMenu = ({
  collapsed,
  label,
  railWidth,
  railLabel,
  trigger,
  triggerClassName,
  children,
  open: openProp,
  onOpenChange,
}) => {
  const [openState, setOpenState] = useState(false)
  const open = openProp ?? openState
  const onOpenChangeRef = useRef(onOpenChange)
  onOpenChangeRef.current = onOpenChange
  const setOpen = useCallback((next) => {
    setOpenState(next)
    if (onOpenChangeRef.current) onOpenChangeRef.current(next)
  }, [])
  const [row, setRow] = useState({ width: 0, height: 0 })
  const rootRef = useRef(null)
  const triggerRef = useRef(null)
  const bodyId = useId()
  const { pathname } = useLocation()

  // the card's closed size IS the row's size, which the rail toggle changes
  useLayoutEffect(() => {
    const el = triggerRef.current
    const measure = () =>
      setRow({ width: el.offsetWidth, height: el.offsetHeight })
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    setOpen(false)
  }, [pathname, collapsed, setOpen])

  useEffect(() => {
    if (!open) return undefined
    const onMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return
      setOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, setOpen])

  const openWidth = collapsed ? railWidth : row.width
  // A popover: two surface steps above whatever it opens on, with the shadow
  // pinned to level 3 so it reads the same wherever it sits.
  const level = Math.min(useSurface() + 2, 8)

  return (
    <div ref={rootRef} className={'sb-menu' + (open ? ' is-open' : '')}>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName + (open ? ' is-open' : '')}
        aria-expanded={open}
        aria-controls={open ? bodyId : undefined}
        aria-label={collapsed ? label : undefined}
        title={collapsed ? label : undefined}
        onClick={() => setOpen(!open)}
      >
        {trigger}
      </button>

      <motion.div
        className={'sb-menu-card ' + surfaceClasses(level, 3)}
        initial={false}
        animate={{ width: open ? openWidth : row.width, opacity: open ? 1 : 0 }}
        transition={{
          width: spring.moderate,
          // closing waits for the body to start folding before the card fades
          opacity: open ? spring.fast : { ...spring.moderate.exit, delay: 0.08 },
        }}
      >
        <AnimatePresence initial={false}>
          {open ? (
            <motion.div
              key="body"
              id={bodyId}
              className="sb-menu-body"
              initial={{ height: 0 }}
              animate={{ height: 'auto' }}
              exit={{ height: 0, transition: spring.moderate.exit }}
              transition={spring.moderate}
            >
              {/* fixed width, so nothing reflows while the card widens */}
              <motion.div
                className="sb-menu-content"
                style={{ width: Math.max(0, openWidth - 2) }}
                initial={{ opacity: 0, y: 6 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  transition: { ...spring.moderate, delay: 0.08 },
                }}
                exit={{ opacity: 0, y: 4, transition: spring.fast.exit }}
              >
                <SurfaceProvider value={level}>{children}</SurfaceProvider>
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>

        <div
          className="sb-menu-tail"
          style={{ height: row.height, paddingLeft: row.width }}
          aria-hidden="true"
        >
          {collapsed ? railLabel : null}
        </div>
      </motion.div>
    </div>
  )
}

export default SidebarMenu
